import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Calendar,
  Briefcase,
  CreditCard,
  Clock,
  LogOut,
  Menu,
  X,
  DollarSign,
  Shield,
  Activity,
  BookOpen,
} from "lucide-react";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/i0b4179m_A_I_Means_Business.jpg";

const sidebarLinks = [
  { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/leads", label: "Leads", icon: Users },
  { href: "/admin/clients", label: "Clients", icon: UserCheck },
  { href: "/admin/appointments", label: "Appointments", icon: Calendar },
  { href: "/admin/engagements", label: "Engagements", icon: Briefcase },
  { href: "/admin/billing", label: "Billing", icon: CreditCard },
  { href: "/admin/pricing", label: "Pricing", icon: DollarSign },
  { href: "/admin/availability", label: "Availability", icon: Clock },
  { href: "/admin/activity", label: "Activity", icon: Activity },
  { href: "/admin/ebook-metrics", label: "Ebook Metrics", icon: BookOpen },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-navy-900 border-r border-border transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 md:static`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
              <img src={LOGO_URL} alt="A.I. Means Business" className="h-8 w-auto bg-white rounded" />
              <span className="font-heading font-semibold text-white text-sm">Admin</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {sidebarLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`admin-nav-${link.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="h-16 border-b border-border bg-navy-900/50 backdrop-blur-sm flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center">
            <button
              className="md:hidden text-white mr-4"
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="font-heading font-semibold text-lg text-white">
              {sidebarLinks.find((l) => l.href === location.pathname)?.label || "Dashboard"}
            </h1>
          </div>

          {/* Admin Account Badge & Logout */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 rounded-lg bg-navy-800/50 border border-slate-700">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-slate-400">Admin Account</span>
                <span className="text-sm font-medium text-white truncate max-w-[150px]">
                  {user?.email || "admin@aimeansbusiness.com"}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="admin-header-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
